export const BINARY_OPS = ['+', '-', '*', '/'];
/** How operators are shown to people. */
export const OP_GLYPHS = { '+': '+', '-': '−', '*': '×', '/': '÷' };
const PRECEDENCE = { '+': 1, '-': 1, '*': 2, '/': 2 };
/** A formula with more nodes, or nested deeper, than this is refused. */
export const MAX_FORMULA_NODES = 200;
export const MAX_FORMULA_DEPTH = 40;
export const FORMULA_MESSAGES = {
    TOO_DEEP: 'The formula is nested too deeply.',
    TOO_LARGE: 'The formula has too many terms.',
    MALFORMED: 'The formula is malformed.',
    KPI_ID: 'A KPI operand is missing its id.',
    KPI_FIELDS: 'A KPI operand has unexpected fields.',
    CONST_VALUE: 'A constant must be a finite number.',
    CONST_FIELDS: 'A constant has unexpected fields.',
    OPERATOR: 'An operator must be one of + − × ÷.',
    BINARY_FIELDS: 'An operation has unexpected fields.',
    UNKNOWN_NODE: 'Unknown formula element.',
    EMPTY: 'The formula is empty.',
    TWO_VALUES: 'Two values in a row — put an operator between them.',
    OPERATOR_FIRST: 'An operator needs a value before it.',
    BRACKET_AFTER_VALUE: 'Put an operator before an opening bracket.',
    EMPTY_BRACKETS: 'A closing bracket needs a value before it.',
    UNBALANCED: 'The brackets do not match.',
    TRAILING_OPERATOR: 'The formula ends with an operator.',
    UNKNOWN_CODE: (code) => `Unknown KPI code "${code}".`,
    UNEXPECTED_CHARACTER: (character) => `Unexpected "${character}" in the formula.`,
};
class FormulaError extends Error {
}
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isBinaryOp = (value) => typeof value === 'string' && BINARY_OPS.includes(value);
/**
 * Strictly validates an untrusted value (a request body, a `jsonb` column) as a formula AST: exactly one of the
 * three node shapes, no extra fields, finite constants, at most `MAX_FORMULA_NODES` nodes and
 * `MAX_FORMULA_DEPTH` levels. Returns a fresh copy, never the input object.
 */
export const parseFormula = (raw) => {
    let nodes = 0;
    const walk = (node, depth) => {
        if (depth > MAX_FORMULA_DEPTH) {
            throw new FormulaError(FORMULA_MESSAGES.TOO_DEEP);
        }
        nodes += 1;
        if (nodes > MAX_FORMULA_NODES) {
            throw new FormulaError(FORMULA_MESSAGES.TOO_LARGE);
        }
        if (!isRecord(node)) {
            throw new FormulaError(FORMULA_MESSAGES.MALFORMED);
        }
        const fields = Object.keys(node).length;
        switch (node.type) {
            case 'kpi':
                if (typeof node.kpiId !== 'string' || node.kpiId === '') {
                    throw new FormulaError(FORMULA_MESSAGES.KPI_ID);
                }
                if (fields !== 2) {
                    throw new FormulaError(FORMULA_MESSAGES.KPI_FIELDS);
                }
                return { type: 'kpi', kpiId: node.kpiId };
            case 'const':
                if (typeof node.value !== 'number' || !Number.isFinite(node.value)) {
                    throw new FormulaError(FORMULA_MESSAGES.CONST_VALUE);
                }
                if (fields !== 2) {
                    throw new FormulaError(FORMULA_MESSAGES.CONST_FIELDS);
                }
                return { type: 'const', value: node.value };
            case 'binary':
                if (!isBinaryOp(node.op)) {
                    throw new FormulaError(FORMULA_MESSAGES.OPERATOR);
                }
                if (fields !== 4) {
                    throw new FormulaError(FORMULA_MESSAGES.BINARY_FIELDS);
                }
                return { type: 'binary', op: node.op, left: walk(node.left, depth + 1), right: walk(node.right, depth + 1) };
            default:
                throw new FormulaError(FORMULA_MESSAGES.UNKNOWN_NODE);
        }
    };
    try {
        return { ok: true, ast: walk(raw, 0) };
    }
    catch (error) {
        return { ok: false, error: error.message };
    }
};
/**
 * Shunting-yard: a token sequence (left-associative, × ÷ before + −, brackets) to an AST. Pure and deterministic;
 * every structural mistake gets a message the builder shows inline. The result passes through `parseFormula` so
 * the size caps hold for built formulas too.
 */
export const tokensToAst = (tokens) => {
    if (tokens.length === 0) {
        return { ok: false, error: FORMULA_MESSAGES.EMPTY };
    }
    const output = [];
    const stack = [];
    let expectOperand = true;
    // The `expectOperand` checks guarantee two operands are on the output whenever an operator is applied.
    const apply = (op) => {
        const right = output.pop();
        const left = output.pop();
        output.push({ type: 'binary', op, left, right });
    };
    try {
        for (const token of tokens) {
            switch (token.kind) {
                case 'kpi':
                case 'const':
                    if (!expectOperand) {
                        throw new FormulaError(FORMULA_MESSAGES.TWO_VALUES);
                    }
                    output.push(token.kind === 'kpi' ? { type: 'kpi', kpiId: token.kpiId } : { type: 'const', value: token.value });
                    expectOperand = false;
                    break;
                case 'op': {
                    if (expectOperand) {
                        throw new FormulaError(FORMULA_MESSAGES.OPERATOR_FIRST);
                    }
                    let top = stack[stack.length - 1];
                    while (top?.kind === 'op' && PRECEDENCE[top.op] >= PRECEDENCE[token.op]) {
                        stack.pop();
                        apply(top.op);
                        top = stack[stack.length - 1];
                    }
                    stack.push({ kind: 'op', op: token.op });
                    expectOperand = true;
                    break;
                }
                case 'lparen':
                    if (!expectOperand) {
                        throw new FormulaError(FORMULA_MESSAGES.BRACKET_AFTER_VALUE);
                    }
                    stack.push({ kind: 'lparen' });
                    break;
                case 'rparen': {
                    if (expectOperand) {
                        throw new FormulaError(FORMULA_MESSAGES.EMPTY_BRACKETS);
                    }
                    let entry = stack.pop();
                    while (entry?.kind === 'op') {
                        apply(entry.op);
                        entry = stack.pop();
                    }
                    if (entry === undefined) {
                        throw new FormulaError(FORMULA_MESSAGES.UNBALANCED);
                    }
                    break;
                }
            }
        }
        if (expectOperand) {
            throw new FormulaError(FORMULA_MESSAGES.TRAILING_OPERATOR);
        }
        for (let entry = stack.pop(); entry !== undefined; entry = stack.pop()) {
            if (entry.kind === 'lparen') {
                throw new FormulaError(FORMULA_MESSAGES.UNBALANCED);
            }
            apply(entry.op);
        }
    }
    catch (error) {
        return { ok: false, error: error.message };
    }
    return parseFormula(output[0]);
};
/** An AST back to tokens, with brackets only where precedence or left-associativity needs them. */
export const astToTokens = (ast) => {
    const tokens = [];
    const walk = (node, minPrecedence) => {
        if (node.type === 'kpi') {
            tokens.push({ kind: 'kpi', kpiId: node.kpiId });
            return;
        }
        if (node.type === 'const') {
            tokens.push({ kind: 'const', value: node.value });
            return;
        }
        const precedence = PRECEDENCE[node.op];
        const bracket = precedence < minPrecedence;
        if (bracket) {
            tokens.push({ kind: 'lparen' });
        }
        walk(node.left, precedence);
        tokens.push({ kind: 'op', op: node.op });
        walk(node.right, precedence + 1);
        if (bracket) {
            tokens.push({ kind: 'rparen' });
        }
    };
    walk(ast, 0);
    return tokens;
};
/* ───────────────────────── text ⇄ AST (codes, for the seed and people) ───────────────────────── */
const TEXT_OPERATORS = { '+': '+', '-': '-', '−': '-', '*': '*', '×': '*', '/': '/', '÷': '/' };
/** A KPI code: a letter, then letters/digits in dash-joined words (`SELL-SOLD`, `SRV-LABOUR-REV`). */
const CODE_PATTERN = /^[A-Za-z][A-Za-z0-9]*(?:-[A-Za-z0-9]+)*/;
const NUMBER_PATTERN = /^(?:\d+(?:\.\d*)?|\.\d+)/;
/**
 * Splits formula text written with KPI codes into builder tokens. Codes are matched greedily, so a subtraction
 * between two codes needs spaces around the minus (`GET-STOCK - GET-PREP`) or the `−` glyph. `idOfCode` maps a
 * code (case-insensitively, upper-cased) to a KPI id, or `undefined` when there is no such KPI.
 */
export const tokenizeFormulaText = (text, idOfCode) => {
    const tokens = [];
    let rest = text.trim();
    while (rest !== '') {
        const character = rest[0];
        const code = CODE_PATTERN.exec(rest)?.[0];
        const number = NUMBER_PATTERN.exec(rest)?.[0];
        if (code !== undefined) {
            const kpiId = idOfCode(code.toUpperCase());
            if (kpiId === undefined) {
                return { ok: false, error: FORMULA_MESSAGES.UNKNOWN_CODE(code.toUpperCase()) };
            }
            tokens.push({ kind: 'kpi', kpiId });
            rest = rest.slice(code.length);
        }
        else if (number !== undefined) {
            tokens.push({ kind: 'const', value: Number(number) });
            rest = rest.slice(number.length);
        }
        else if (TEXT_OPERATORS[character] !== undefined) {
            tokens.push({ kind: 'op', op: TEXT_OPERATORS[character] });
            rest = rest.slice(1);
        }
        else if (character === '(' || character === ')') {
            tokens.push({ kind: character === '(' ? 'lparen' : 'rparen' });
            rest = rest.slice(1);
        }
        else {
            return { ok: false, error: FORMULA_MESSAGES.UNEXPECTED_CHARACTER(character) };
        }
        rest = rest.trimStart();
    }
    return { ok: true, tokens };
};
/** `SELL-SOLD / SELL-LEADS * 100` → AST, through the same shunting-yard as the builder. */
export const parseFormulaText = (text, idOfCode) => {
    const tokenized = tokenizeFormulaText(text, idOfCode);
    return tokenized.ok ? tokensToAst(tokenized.tokens) : tokenized;
};
/** A constant as people read it: `100`, `0.5`, `-2`. */
const constText = (value) => String(value);
/** A token as the builder shows it. */
export const tokenLabel = (token, labelOf) => {
    switch (token.kind) {
        case 'kpi':
            return labelOf(token.kpiId);
        case 'const':
            return constText(token.value);
        case 'op':
            return OP_GLYPHS[token.op];
        case 'lparen':
            return '(';
        case 'rparen':
            return ')';
    }
};
/**
 * An AST printed for people, with minimal brackets and the operator glyphs: `Vehicles sold ÷ Leads generated × 100`
 * with names, `SELL-SOLD ÷ SELL-LEADS × 100` with codes.
 */
export const formatFormula = (ast, labelOf) => astToTokens(ast)
    .map((token) => tokenLabel(token, labelOf))
    .join(' ')
    .replace(/\( /g, '(')
    .replace(/ \)/g, ')');
/** The distinct KPI ids the formula references directly, in first-use order. */
export const collectKpiRefs = (ast) => {
    const refs = new Set();
    const walk = (node) => {
        if (node.type === 'kpi') {
            refs.add(node.kpiId);
        }
        else if (node.type === 'binary') {
            walk(node.left);
            walk(node.right);
        }
    };
    walk(ast);
    return [...refs];
};
/* ───────────────────────── evaluation ───────────────────────── */
const finiteOrNull = (value) => (Number.isFinite(value) ? value : null);
/**
 * The formula's value for one week. `resolve(kpiId)` gives each input's value for that week — `null` for a blank,
 * an inactive or a restricted source. Full precision, no rounding; `null` (never 0, NaN or Infinity) when any input
 * is blank, a divisor is 0, or the result overflows.
 */
export const evaluateFormula = (ast, resolve) => {
    const evaluate = (node) => {
        if (node.type === 'const') {
            return node.value;
        }
        if (node.type === 'kpi') {
            const value = resolve(node.kpiId);
            return typeof value === 'number' ? finiteOrNull(value) : null;
        }
        const left = evaluate(node.left);
        const right = left === null ? null : evaluate(node.right);
        if (left === null || right === null) {
            return null;
        }
        switch (node.op) {
            case '+':
                return finiteOrNull(left + right);
            case '-':
                return finiteOrNull(left - right);
            case '*':
                return finiteOrNull(left * right);
            case '/':
                return right === 0 ? null : finiteOrNull(left / right);
        }
    };
    return evaluate(ast);
};
export const PLAIN_UNITS = ['NUMBER', 'PERCENTAGE', 'RATIO', 'DECIMAL'];
export const UNIT_MESSAGES = {
    TEXT_SOURCE: (name) => `"${name}" is a Text KPI and cannot be used in a formula.`,
    UNKNOWN_SOURCE: 'A referenced KPI could not be found.',
    TEXT_DECLARED: 'A calculated KPI cannot have the Text unit.',
    ADD_MISMATCH: (left, right, op) => `Cannot ${op === '+' ? 'add' : 'subtract'} ${left} and ${right} — the units differ.`,
    MULTIPLY_UNITS: (left, right) => `Cannot multiply ${left} by ${right} — the product has no meaningful unit.`,
    DIVIDE_MISMATCH: (left, right) => `Cannot divide ${left} by ${right} — the units are incompatible.`,
    DIVIDE_PLAIN_BY_UNIT: (right) => `Cannot divide a plain number by ${right}.`,
    PLAIN_FOR_UNIT: (declared) => `This formula produces a plain number, but the KPI's unit is ${declared}. Choose Number, Percentage, Ratio or Decimal.`,
    UNIT_MISMATCH: (produced, declared) => `This formula produces ${produced}, which does not match the KPI's unit ${declared}.`,
    AMBIGUOUS_PERCENT: 'A division declared as Percentage usually needs × 100 — check the formula is intentional.',
    PLAIN_NUMBER_MIX: (unit, op) => `${op === '+' ? 'Adding' : 'Subtracting'} a plain number ${op === '+' ? 'to' : 'and'} ${unit} — check the formula is intentional.`,
};
const PLAIN = { kind: 'plain' };
/** `a plain number`, `JOD`, `Days`, `Hours`. */
export const dimensionLabel = (dimension) => {
    if (dimension.kind === 'plain') {
        return 'a plain number';
    }
    if (dimension.unit === 'CURRENCY') {
        return dimension.currency ?? 'Currency';
    }
    return dimension.unit === 'DAYS' ? 'Days' : 'Hours';
};
const UNIT_NAMES = {
    NUMBER: 'Number',
    PERCENTAGE: 'Percentage',
    CURRENCY: 'Currency',
    DAYS: 'Days',
    HOURS: 'Hours',
    RATIO: 'Ratio',
    DECIMAL: 'Decimal',
    TEXT: 'Text',
};
/** A declared unit as people read it: `Percentage`, `Days`, or the currency code for Currency (`JOD`). */
export const declaredUnitLabel = (unit, currency) => (unit === 'CURRENCY' && currency ? currency : UNIT_NAMES[unit]);
/** The dimension of a KPI's unit; `null` for Text, which has none. */
export const dimensionOfUnit = (unit, currency) => {
    if (unit === 'TEXT') {
        return null;
    }
    if (PLAIN_UNITS.includes(unit)) {
        return PLAIN;
    }
    return { kind: 'unit', unit: unit, currency: unit === 'CURRENCY' ? currency : null };
};
export const sameDimension = (a, b) => a.kind === 'plain' || b.kind === 'plain' ? a.kind === b.kind : a.unit === b.unit && a.currency === b.currency;
/**
 * The dimension algebra (the "inference table", plan §3.1):
 *
 * | op    | plain ∘ plain | unit ∘ plain        | plain ∘ unit        | unit ∘ same unit | unit ∘ other unit |
 * |-------|---------------|---------------------|---------------------|------------------|-------------------|
 * | + −   | plain         | unit + **warning**  | unit + **warning**  | unit             | error             |
 * | ×     | plain         | unit                | unit                | error            | error             |
 * | ÷     | plain         | unit (per-item)     | error               | plain (a ratio)  | error             |
 *
 * The produced dimension is then checked against the declared unit: a plain result needs a plain unit (and a bare
 * top-level division declared as Percentage is a **warning** — it usually needs × 100); a unit result must equal
 * the declared unit and currency exactly. Text anywhere is an error.
 */
export const inferFormulaUnit = (ast, declared, unitOf) => {
    const warnings = [];
    const infer = (node) => {
        if (node.type === 'const') {
            return PLAIN;
        }
        if (node.type === 'kpi') {
            const source = unitOf(node.kpiId);
            if (!source) {
                throw new FormulaError(UNIT_MESSAGES.UNKNOWN_SOURCE);
            }
            const dimension = dimensionOfUnit(source.unit, source.currency);
            if (!dimension) {
                throw new FormulaError(UNIT_MESSAGES.TEXT_SOURCE(source.name));
            }
            return dimension;
        }
        const left = infer(node.left);
        const right = infer(node.right);
        const leftHasUnit = left.kind === 'unit';
        const rightHasUnit = right.kind === 'unit';
        switch (node.op) {
            case '+':
            case '-':
                if (leftHasUnit && rightHasUnit) {
                    if (!sameDimension(left, right)) {
                        throw new FormulaError(UNIT_MESSAGES.ADD_MISMATCH(dimensionLabel(left), dimensionLabel(right), node.op));
                    }
                    return left;
                }
                if (leftHasUnit || rightHasUnit) {
                    const unit = leftHasUnit ? left : right;
                    warnings.push({ code: 'PLAIN_NUMBER_MIX', message: UNIT_MESSAGES.PLAIN_NUMBER_MIX(dimensionLabel(unit), node.op) });
                    return unit;
                }
                return PLAIN;
            case '*':
                if (leftHasUnit && rightHasUnit) {
                    throw new FormulaError(UNIT_MESSAGES.MULTIPLY_UNITS(dimensionLabel(left), dimensionLabel(right)));
                }
                return leftHasUnit ? left : right;
            case '/':
                if (leftHasUnit && rightHasUnit) {
                    if (!sameDimension(left, right)) {
                        throw new FormulaError(UNIT_MESSAGES.DIVIDE_MISMATCH(dimensionLabel(left), dimensionLabel(right)));
                    }
                    return PLAIN;
                }
                if (rightHasUnit) {
                    throw new FormulaError(UNIT_MESSAGES.DIVIDE_PLAIN_BY_UNIT(dimensionLabel(right)));
                }
                return left;
        }
    };
    const declaredDimension = dimensionOfUnit(declared.unit, declared.currency);
    if (!declaredDimension) {
        return { ok: false, error: UNIT_MESSAGES.TEXT_DECLARED, warnings };
    }
    let produces;
    try {
        produces = infer(ast);
    }
    catch (error) {
        return { ok: false, error: error.message, warnings };
    }
    if (produces.kind === 'plain') {
        if (declaredDimension.kind !== 'plain') {
            return { ok: false, error: UNIT_MESSAGES.PLAIN_FOR_UNIT(declaredUnitLabel(declared.unit, declared.currency)), warnings };
        }
        if (declared.unit === 'PERCENTAGE' && ast.type === 'binary' && ast.op === '/') {
            warnings.push({ code: 'AMBIGUOUS_PERCENT', message: UNIT_MESSAGES.AMBIGUOUS_PERCENT });
        }
        return { ok: true, produces, warnings };
    }
    if (!sameDimension(produces, declaredDimension)) {
        return { ok: false, error: UNIT_MESSAGES.UNIT_MISMATCH(dimensionLabel(produces), declaredUnitLabel(declared.unit, declared.currency)), warnings };
    }
    return { ok: true, produces, warnings };
};
//# sourceMappingURL=formula.js.map